<template>
  <div class="page page-week-report">
    <!-- Top Bar (consistent with DayReport) -->
    <div class="custom-top-bar">
      <div class="brand-area">
        <img :src="icons.logo" class="brand-logo" alt="logo" />
        <span class="brand-text">Kukukao</span>
      </div>
      <button class="btn-download" @click="handleOpenApp">下载app</button>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <div class="loading-spinner"></div>
      <div class="loading-text">加载中...</div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="error-container">
      <div class="error-text">{{ error }}</div>
      <button class="btn-retry" @click="fetchWeekReport">重试</button>
    </div>

    <div v-else class="content-wrapper">
      <!-- Orange Gradient Header Background -->
      <div class="header-bg"></div>

      <div class="phone">
        <div class="phone-inner">
          <!-- Header Content (on orange gradient) -->
          <div class="report-header">
            <div class="user-info">
              <div class="avatar">
                <img v-if="reportData.avatar" :src="reportData.avatar" />
                <div v-else class="avatar-placeholder">头像</div>
              </div>
              <div class="greetings">
                <div class="hi">Hi! {{ reportData.nickname || '用户' }}</div>
                <div class="sub">{{ reportData.summary || '过去的一周非常棒！' }}</div>
              </div>
            </div>

            <div class="keywords-block" v-if="reportData.keywords && reportData.keywords.length">
              <div class="date-label">（{{ reportData.startDate | formatDate }}-{{ reportData.endDate | formatDate }}）本周的关键词是：</div>
              <div class="tags">
                <div
                  v-for="(keyword, index) in reportData.keywords"
                  :key="index"
                  class="tag"
                  :class="getKeywordColor(index)"
                >
                  #{{ keyword }}
                </div>
              </div>
            </div>
            <div class="keywords-block" v-else>
               <div class="date-label">（{{ reportData.startDate | formatDate }}-{{ reportData.endDate | formatDate }}）</div>
            </div>
          </div>

          <!-- Experience Card (floating over header) -->
          <div class="exp-card">
            <div class="exp-top">
              <div class="exp-item">
                <span class="exp-label">本周学习经验：</span>
                <span class="exp-num gold">{{ reportData.totalExp || 0 }}</span>
              </div>
              <div class="exp-item">
                <span class="exp-label">连续打卡天数：</span>
                <span class="exp-num red">{{ reportData.streakDays || 0 }}</span>
              </div>
            </div>
            <div class="exp-divider"></div>
            <div class="exp-bottom">
              <div class="exp-stat">
                <img :src="icons.answer" class="exp-stat-icon" alt="" />
                <div class="exp-stat-text">
                  <div class="exp-stat-label">较上周增长</div>
                  <div class="exp-stat-val red">{{ (reportData.expGrowthRate || 0).toFixed(1) }}%</div>
                </div>
              </div>
              <div class="exp-stat-divider"></div>
              <div class="exp-stat">
                <img :src="icons.mascot" class="exp-stat-icon" alt="" />
                <div class="exp-stat-text">
                  <div class="exp-stat-label">超过全国</div>
                  <div class="exp-stat-val red">{{ (reportData.streakRankPercent || 0).toFixed(0) }}%<span class="small">用户</span></div>
                </div>
              </div>
            </div>
          </div>

          <!-- 基础/真题切换 Tab -->
          <div class="tab-switch" v-if="hasRealQuestionData">
            <div
              class="tab-item"
              :class="{ active: activeTab === 'basic' }"
              @click="activeTab = 'basic'"
            >基础</div>
            <div
              class="tab-item"
              :class="{ active: activeTab === 'real' }"
              @click="activeTab = 'real'"
            >真题</div>
          </div>

          <!-- Distribution Card - 动态饼图 -->
          <div class="section distribution-card" v-if="currentHasDistribution">
            <div class="card-tag">本周投入分布</div>
            <div class="dist-content">
              <div class="legend">
                <div class="legend-item" v-for="(rate, subject) in currentSubjectDistribution" :key="subject">
                  <span class="dot" :style="{ background: getSubjectColor(subject) }"></span>
                  <span class="legend-name">{{ subject }}占比：</span>
                  <span class="legend-val" :style="{ color: getSubjectColor(subject) }">{{ rate.toFixed(1) }}%</span>
                </div>
              </div>
              <div class="chart-wrap">
                <svg class="pie-chart-svg" viewBox="0 0 120 120">
                  <circle
                    v-for="(segment, idx) in currentPieSegments"
                    :key="idx"
                    cx="60" cy="60" r="45"
                    fill="none"
                    :stroke="segment.color"
                    stroke-width="18"
                    :stroke-dasharray="segment.dashArray"
                    :stroke-dashoffset="segment.dashOffset"
                    stroke-linecap="round"
                    transform="rotate(-90 60 60)"
                  />
                  <text x="60" y="60" text-anchor="middle" dominant-baseline="central" class="pie-pct">{{ currentTotalDistribution }}%</text>
                </svg>
              </div>
            </div>
          </div>

          <!-- Base Stats Grid -->
          <div class="section base-stats">
            <!-- 基础模式 -->
            <template v-if="activeTab === 'basic'">
              <div class="stat-box">
                <div class="stat-box-icon-wrap">
                  <img :src="icons.level" class="stat-box-icon" alt="" />
                </div>
                <div class="stat-box-num">{{ reportData.stageCount || 0 }}</div>
                <div class="stat-box-label">闯关次数</div>
              </div>
              <div class="stat-box">
                <div class="stat-box-icon-wrap">
                  <img :src="icons.studyTime" class="stat-box-icon" alt="" />
                </div>
                <div class="stat-box-num">{{ reportData.studyDuration || 0 }}<span class="unit">小时</span></div>
                <div class="stat-box-label">学习时长</div>
              </div>
              <div class="stat-box">
                <div class="stat-box-icon-wrap">
                  <img :src="icons.growth" class="stat-box-icon" alt="" />
                </div>
                <div class="stat-box-num">{{ reportData.questionCount || 0 }}</div>
                <div class="stat-box-label">答题数</div>
              </div>
            </template>
            <!-- 真题模式 -->
            <template v-else>
              <div class="stat-box">
                <div class="stat-box-icon-wrap">
                  <img :src="icons.accuracy" class="stat-box-icon" alt="" />
                </div>
                <div class="stat-box-num">{{ realQuestionAccuracy }}%</div>
                <div class="stat-box-label">正确率</div>
              </div>
              <div class="stat-box">
                <div class="stat-box-icon-wrap">
                  <img :src="icons.studyTime" class="stat-box-icon" alt="" />
                </div>
                <div class="stat-box-num">{{ realQuestionStudyDuration }}<span class="unit">小时</span></div>
                <div class="stat-box-label">学习时长</div>
              </div>
              <div class="stat-box">
                <div class="stat-box-icon-wrap">
                  <img :src="icons.growth" class="stat-box-icon" alt="" />
                </div>
                <div class="stat-box-num">{{ realQuestionAnswerCount || 0 }}</div>
                <div class="stat-box-label">答题数</div>
              </div>
            </template>
          </div>

          <!-- Learning Diagnosis：mascot 在标题左侧 -->
          <div class="section guide-card" v-if="reportData.diagnosis">
            <div class="guide-header">
              <div class="guide-title-wrap">
                <img :src="icons.mascotHead" class="mascot-head" alt="mascot" />
                <span class="guide-title">kuku学习诊断</span>
              </div>
            </div>

            <div class="guide-body">
               <div v-if="parsedDiagnosis">
                  <!-- weekGet 知识点 -->
                  <div class="diag-section" v-if="parsedDiagnosis.weekGet && parsedDiagnosis.weekGet.length">
                    <div class="diag-title">
                      <img :src="icons.diagnosisEasy" class="diag-icon-img" />
                      <span>本周Get知识点</span>
                    </div>

                    <div v-for="(item, idx) in parsedDiagnosis.weekGet" :key="idx">
                      <div class="subject-label" :style="{ color: getSubjectColor(item.subjectName) }">{{ item.subjectName }}</div>
                      <div class="subject-desc" v-html="formatDiagnosisText(item.summary)"></div>
                    </div>
                  </div>

                  <!-- specializedTraining kuku建议 -->
                  <div class="training-block" v-if="parsedDiagnosis.specializedTraining">
                    <div class="training-title">
                      <img :src="icons.muscle" class="training-icon" />
                      <strong>kuku分析</strong>
                    </div>
                    <div class="training-desc" v-html="formatDiagnosisText(parsedDiagnosis.specializedTraining)"></div>
                  </div>
               </div>

               <!-- Fallback -->
               <div v-else class="simple-diagnosis">
                 {{ reportData.diagnosis }}
               </div>
            </div>
          </div>

          <!-- Quote -->
          <div class="section quote-box" v-if="reportData.encouragingWords">
            <div class="quote-mark">"</div>
            <div class="quote-text">
              {{ reportData.encouragingWords }}
            </div>
          </div>

          <!-- Bottom Button -->
          <div class="bottom-btn-wrapper">
            <div class="bottom-btn" @click="handleOpenApp">
              打开app查看
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { homeImages } from '../homeImages'

// Import assets
import logo from '@/assets/logo.svg'
import iconGrowth from '@/assets/week/icon_growth.svg'
import iconBeat from '@/assets/week/icon_beat.svg'
import iconPieChart from '@/assets/week/icon_pie_chart.svg'
import iconLevel from '@/assets/week/icon_level.svg'
import iconStudyTime from '@/assets/week/icon_study_time.svg'
import iconAnswer from '@/assets/week/icon_answer.svg'
import iconMascot from '@/assets/week/icon_mascot.svg'
import iconMuscle from '@/assets/week/icon_muscle.svg'
// icon_accuracy.svg 暂不存在，使用 icon_answer.svg 替代
import iconAccuracy from '@/assets/week/icon_answer.svg'
import mascotHead from '@/assets/day/mascot_head.svg'
import iconDiagnosisEasy from '@/assets/day/icon_diagnosis_easy.svg'
import { openApp } from '@/utils/openApp'

export default {
  name: 'HomeWeekReport',
  props: {
    query: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      images: homeImages,
      icons: {
        logo,
        growth: iconGrowth,
        beat: iconBeat,
        pieChart: iconPieChart,
        level: iconLevel,
        studyTime: iconStudyTime,
        answer: iconAnswer,
        mascot: iconMascot,
        muscle: iconMuscle,
        accuracy: iconAccuracy,
        mascotHead: mascotHead,
        diagnosisEasy: iconDiagnosisEasy
      },
      loading: true,
      error: null,
      reportData: {},
      activeTab: 'basic' // 'basic' 或 'real'
    }
  },
  computed: {
    reportId() {
      return this.query.id || (this.$route && this.$route.query && this.$route.query.id) || null
    },
    hasDistribution() {
      return this.reportData.subjectDistribution && Object.keys(this.reportData.subjectDistribution).length > 0
    },
    // 是否有真题数据
    hasRealQuestionData() {
      return !!(this.reportData.realQuestionInvestment && this.reportData.realQuestionInvestment.totalExp !== undefined)
    },
    // 当前 Tab 的学科分布（百分比）
    currentSubjectDistribution() {
      if (this.activeTab === 'real') {
        // 真题模式：从 realQuestionInvestment 的经验值计算百分比
        const inv = this.reportData.realQuestionInvestment
        if (!inv) return {}
        const paperOne = inv.paperOneExp || 0
        const paperTwo = inv.paperTwoExp || 0
        const total = paperOne + paperTwo
        if (total === 0) return {}
        const dist = {}
        if (paperOne > 0) {
          dist['卷一'] = Number(((paperOne / total) * 100).toFixed(1))
        }
        if (paperTwo > 0) {
          dist['卷二'] = Number(((paperTwo / total) * 100).toFixed(1))
        }
        return dist
      }
      return this.reportData.subjectDistribution || {}
    },
    // 当前 Tab 是否有分布数据
    currentHasDistribution() {
      return Object.keys(this.currentSubjectDistribution).length > 0
    },
    // 当前 Tab 的饼图数据
    currentPieSegments() {
      const dist = this.currentSubjectDistribution
      const colors = this.activeTab === 'real'
        ? this.reportData.realQuestionSubjectColor
        : this.reportData.subjectColor
      if (!dist || Object.keys(dist).length === 0) return []

      const circumference = 2 * Math.PI * 45
      const segments = []
      let accumulatedOffset = 0

      for (const [subject, percentage] of Object.entries(dist)) {
        const length = (percentage / 100) * circumference
        const color = colors && colors[subject] ? colors[subject] : '#FFD54F'
        segments.push({
          color,
          dashArray: `${length} ${circumference - length}`,
          dashOffset: -accumulatedOffset
        })
        accumulatedOffset += length
      }

      return segments
    },
    // 当前 Tab 的总分布百分比
    currentTotalDistribution() {
      const dist = this.currentSubjectDistribution
      if (!dist || Object.keys(dist).length === 0) return 0
      return Math.round(Object.values(dist).reduce((sum, v) => sum + v, 0))
    },
    // 真题正确率
    realQuestionAccuracy() {
      const inv = this.reportData.realQuestionInvestment
      if (!inv || inv.accuracy === undefined) return 0
      const val = Number(inv.accuracy)
      return Number.isInteger(val) ? val : val.toFixed(1)
    },
    // 真题学习时长（小时）
    realQuestionStudyDuration() {
      const inv = this.reportData.realQuestionInvestment
      if (!inv || !inv.studyDurationSec) return 0
      return (inv.studyDurationSec / 3600).toFixed(1)
    },
    // 真题答题数
    realQuestionAnswerCount() {
      const inv = this.reportData.realQuestionInvestment
      return inv ? (inv.answerCount || 0) : 0
    },
    parsedDiagnosis() {
      if (!this.reportData.diagnosis) return null
      try {
        if (typeof this.reportData.diagnosis === 'string') {
          if (this.reportData.diagnosis.trim().startsWith('{')) {
            return JSON.parse(this.reportData.diagnosis)
          }
        } else if (typeof this.reportData.diagnosis === 'object') {
          return this.reportData.diagnosis
        }
        return null
      } catch (e) {
        return null
      }
    },
    trainingKeywords() {
      if (!this.parsedDiagnosis || !this.parsedDiagnosis.specializedTraining) return ''
      // 从 specializedTraining 中提取 **...** 关键词作为标题
      const matches = this.parsedDiagnosis.specializedTraining.match(/\*\*(.*?)\*\*/g)
      if (matches && matches.length > 0) {
        return matches.slice(0, 2).map(m => m.replace(/\*\*/g, '')).join('、')
      }
      return ''
    },
    pieSegments() {
      const dist = this.reportData.subjectDistribution
      const colors = this.reportData.subjectColor
      if (!dist) return []

      const circumference = 2 * Math.PI * 45 // r=45, ≈ 282.74
      const segments = []
      let accumulatedOffset = 0

      for (const [subject, percentage] of Object.entries(dist)) {
        const length = (percentage / 100) * circumference
        const color = colors && colors[subject] ? colors[subject] : '#FFD54F'
        segments.push({
          color,
          dashArray: `${length} ${circumference - length}`,
          dashOffset: -accumulatedOffset
        })
        accumulatedOffset += length
      }

      return segments
    },
    totalDistribution() {
      const dist = this.reportData.subjectDistribution
      if (!dist) return 0
      return Math.round(Object.values(dist).reduce((sum, v) => sum + v, 0))
    }
  },
  filters: {
    formatDate(dateStr) {
      if (!dateStr) return ''
      try {
        const date = new Date(dateStr)
        return `${date.getMonth() + 1}月${date.getDate()}日`
      } catch (e) {
        return dateStr
      }
    }
  },
  mounted() {
    this.fetchWeekReport()
  },
  methods: {
     async fetchWeekReport() {
      if (!this.reportId) {
        this.error = '缺少报告ID参数'
        this.loading = false
        return
      }

      this.loading = true
      this.error = null

      try {
        const response = await fetch(`${process.env.VUE_APP_API_BASE_URL || ''}/share/getWeekReport?id=${this.reportId}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          }
        })
        const result = await response.json()

        if (result.code === 200 && result.data) {
          this.reportData = result.data
        } else {
          this.error = result.message || '获取数据失败'
        }
      } catch (err) {
        console.error('获取周报失败:', err)
        this.error = '网络错误，请稍后重试'
      } finally {
        this.loading = false
      }
    },
    getKeywordColor(index) {
      const colors = ['tag-pink', 'tag-blue', 'tag-orange']
      return colors[index % colors.length]
    },
    getSubjectColor(subject) {
      const colors = this.reportData.subjectColor
      if (colors && colors[subject]) return colors[subject]
      // 默认颜色
      const defaults = { '民法': '#FFD94E', '刑法': '#F8876E' }
      return defaults[subject] || '#FFB300'
    },
    formatDiagnosisText(text) {
      if (!text) return ''
      // 先转义 HTML 特殊字符，防止 XSS
      let html = String(text)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
      // 处理 **加粗**，v-html 插入的节点需要配合 ::v-deep 样式生效
      html = html.replace(/\*\*(.*?)\*\*/g, '<strong class="diag-strong">$1</strong>')
      // 处理颜色标签 {{文字|颜色}}
      html = html.replace(/\{\{(.+?)\|([^}]+)\}\}/g, function (_, text, color) {
        var safe = String(color).trim()
        if (/^(#[0-9a-fA-F]{3,8}|rgb\([^)]+\)|rgba\([^)]+\)|[a-zA-Z]+)$/.test(safe)) {
          return '<span style="color:' + safe + '">' + text + '</span>'
        }
        return text
      })
      // 处理无序列表
      const lines = html.split('\n')
      let result = []
      let inList = false
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i]
        const listMatch = line.match(/^\s*[-*]\s+(.*)$/)
        if (listMatch) {
          if (!inList) {
            result.push('<ul class="diag-list">')
            inList = true
          }
          result.push('<li>' + listMatch[1] + '</li>')
        } else {
          if (inList) {
            result.push('</ul>')
            inList = false
          }
          const indented = line.replace(/^(\s+)/, function (m) {
            return new Array(m.length + 1).join('&nbsp;')
          })
          result.push(indented)
        }
      }
      if (inList) result.push('</ul>')
      html = result.join('\n')
      html = html.replace(/\n/g, '<br/>')
      html = html.replace(/(<\/ul>)<br\/>/g, '$1')
      html = html.replace(/<br\/>(<li>)/g, '$1')
      return html
    },
    handleOpenApp() {
      openApp()
    }
  }
}
</script>

<style lang="scss" scoped>
/* ========== Page ========== */
.page-week-report {
  min-height: 100vh;
  background: #F5F5F5;
  padding-bottom: 30px;
  padding-top: calc(56px + env(safe-area-inset-top, 0px)); /* 为固定头部留出空间 */
  position: relative;
  overflow-x: hidden;
  width: 100%;
}

/* ========== Top Bar：固定顶部，不随页面滚动 ========== */
.custom-top-bar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: calc(12px + env(safe-area-inset-top, 0px)) 20px 12px 20px;
  background: #fff;
}

.brand-area {
  display: flex;
  align-items: center;

  .brand-logo {
    width: 32px;
    height: 32px;
    margin-right: 8px;
  }

  .brand-text {
    font-size: 18px;
    font-weight: 700;
    color: #f15b5b;
  }
}

.btn-download {
  background: #f15b5b;
  color: #fff;
  border: none;
  border-radius: 99px;
  padding: 6px 16px;
  font-size: 13px;
  font-weight: 600;
}

/* ========== Orange Gradient Header BG ========== */
.header-bg {
  position: absolute;
  top: 0; /* 头部已固定，背景从内容区顶部开始 */
  left: 0;
  width: 100%;
  height: 320px;
  background: linear-gradient(180deg, #FF8A65 0%, #FF7043 50%, #F5F5F5 100%);
  z-index: 0;
}

/* ========== Phone Container ========== */
.phone {
  padding: 0 12px 12px;
  position: relative;
  z-index: 1;
}

.phone-inner {
  position: relative;
}

/* ========== Section Base ========== */
.section {
  background: #fff;
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 12px;
}

/* ========== Header Content (on orange gradient) ========== */
.report-header {
  padding: 20px 8px 70px;
  color: #fff;
}

.user-info {
  display: flex;
  align-items: center;
  margin-bottom: 20px;

  .avatar {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    background: #fff;
    margin-right: 14px;
    overflow: hidden;
    border: 3px solid rgba(255, 255, 255, 0.4);
    flex-shrink: 0;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .avatar-placeholder {
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #ccc;
      font-size: 12px;
    }
  }

  .greetings {
    .hi {
      font-size: 17px;
      font-weight: 600;
      margin-bottom: 4px;
    }

    .sub {
      font-size: 20px;
      font-weight: 800;
    }
  }
}

.keywords-block {
  .date-label {
    font-size: 12px;
    opacity: 0.9;
    margin-bottom: 12px;
  }

  .tags {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
  }

  .tag {
    padding: 6px 14px;
    background: #fff;
    border-radius: 99px;
    font-size: 13px;
    font-weight: 700;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

    &.tag-pink {
      color: #FF8A80;
    }

    &.tag-blue {
      color: #42A5F5;
    }

    &.tag-orange {
      color: #FFB74D;
    }
  }
}

/* ========== Experience Card (floating) ========== */
.exp-card {
  background: #fff;
  border-radius: 20px;
  padding: 20px;
  margin-top: -50px;
  margin-bottom: 12px;
  position: relative;
  z-index: 2;
  box-shadow: 0 8px 24px rgba(255, 112, 67, 0.12);
}

.exp-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.exp-item {
  display: flex;
  align-items: baseline;

  .exp-label {
    font-size: 13px;
    font-weight: 600;
    color: #333;
  }

  .exp-num {
    font-size: 24px;
    font-weight: 800;
    margin-left: 4px;

    &.gold {
      color: #FFB74D;
    }

    &.red {
      color: #F25C5C;
    }
  }
}

.exp-divider {
  height: 1px;
  background: #F0F0F0;
  margin-bottom: 16px;
}

.exp-bottom {
  display: flex;
  align-items: center;
}

.exp-stat {
  flex: 1;
  display: flex;
  align-items: center;

  .exp-stat-icon {
    width: 40px;
    height: 40px;
    margin-right: 10px;
    object-fit: contain;
  }

  .exp-stat-text {
    .exp-stat-label {
      font-size: 11px;
      color: #999;
      margin-bottom: 2px;
    }

    .exp-stat-val {
      font-size: 16px;
      font-weight: 800;

      &.red {
        color: #F25C5C;
      }

      .small {
        font-size: 11px;
        font-weight: 500;
        color: #F25C5C;
      }
    }
  }
}

.exp-stat-divider {
  width: 1px;
  height: 32px;
  background: #EEEEEE;
  margin: 0 12px;
  flex-shrink: 0;
}

/* ========== Distribution Card ========== */
.distribution-card {
  .card-tag {
    display: inline-block;
    font-size: 13px;
    font-weight: 700;
    color: #FF8A65;
    background: rgba(255, 138, 101, 0.1);
    padding: 6px 12px;
    border-radius: 8px;
    margin-bottom: 16px;
  }

  .dist-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .legend {
    .legend-item {
      display: flex;
      align-items: center;
      margin-bottom: 14px;
      font-size: 13px;
      color: #333;
      font-weight: 600;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  .dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    margin-right: 8px;
    flex-shrink: 0;

    &.dot-yellow {
      background: #FFD54F;
    }

    &.dot-red {
      background: #EF5350;
    }
  }

  .legend-name {
    margin-right: 2px;
  }

  .legend-val {
    font-weight: 700;

    &.yellow {
      color: #FFB300;
    }

    &.red {
      color: #EF5350;
    }
  }

  .chart-wrap {
    width: 100px;
    height: 100px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    .pie-chart-svg {
      width: 100%;
      height: 100%;
    }

    .pie-pct {
      font-size: 16px;
      font-weight: 800;
      fill: #333;
      font-family: -apple-system, BlinkMacSystemFont, sans-serif;
    }
  }
}

/* ========== Base Stats Grid ========== */
.base-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  background: transparent;
  padding: 0;
  margin-bottom: 12px;
}

.stat-box {
  background: #fff;
  border-radius: 16px;
  padding: 16px 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);

  .stat-box-icon-wrap {
    width: 32px;
    height: 32px;
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .stat-box-icon {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  .stat-box-num {
    font-size: 20px;
    font-weight: 800;
    color: #FF7043;
    margin-bottom: 4px;
    line-height: 1.2;

    .unit {
      font-size: 11px;
      font-weight: 600;
      color: #FF7043;
      margin-left: 2px;
    }
  }

  .stat-box-label {
    font-size: 12px;
    font-weight: 600;
    color: #333;
  }
}

/* ========== Learning Diagnosis - mascot 在标题左侧 ========== */
.guide-card {
  position: relative !important;
  overflow: visible !important;

  .guide-header {
    display: flex;
    align-items: center;
    margin-bottom: 16px;

    .guide-title-wrap {
      display: flex;
      align-items: center;

      .mascot-head {
        width: 40px;
        height: 40px;
        object-fit: contain;
        margin-right: 10px;
        flex-shrink: 0;
      }

      .guide-title {
        font-size: 16px;
        font-weight: 800;
        color: #FF7043;
      }
    }
  }

  .guide-body {
    // Inner content area
  }

  .diag-section {
    margin-bottom: 16px;
  }

  .diag-title {
    display: flex;
    align-items: center;
    font-size: 15px;
    font-weight: 700;
    color: #333;
    margin-bottom: 14px;

    .diag-icon-img {
      width: 22px;
      height: 22px;
      margin-right: 8px;
      object-fit: contain;
    }
  }

  .subject-label {
    font-size: 14px;
    font-weight: 700;
    margin-bottom: 6px;
    margin-top: 12px;

    &:first-of-type {
      margin-top: 0;
    }
  }

  .subject-desc {
    font-size: 13px;
    color: #555;
    line-height: 1.7;
    text-align: justify;
    margin-bottom: 4px;

    ::v-deep .diag-strong {
      color: #333;
      font-weight: 800;
    }
  }

  .training-block {
    background: #FFF3F0;
    border-radius: 12px;
    padding: 16px;
    margin-top: 16px;

    .training-title {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      font-size: 14px;
      font-weight: 700;
      color: #333;
      margin-bottom: 10px;

      .training-icon {
        width: 22px;
        height: 22px;
        margin-right: 8px;
        object-fit: contain;
      }

      strong {
        color: #333;
        font-weight: 800;
      }
    }

    .training-desc {
      font-size: 13px;
      color: #555;
      line-height: 1.7;
      text-align: justify;

      ::v-deep .diag-strong {
        color: #333;
        font-weight: 800;
      }

      ::v-deep .diag-list {
        margin: 6px 0;
        padding-left: 0;
        list-style: none;

        li {
          position: relative;
          padding-left: 14px;
          margin-bottom: 6px;

          &::before {
            content: '';
            position: absolute;
            left: 0;
            top: 8px;
            width: 5px;
            height: 5px;
            border-radius: 50%;
            background: #FF7043;
          }
        }
      }
    }
  }
}

/* ========== Quote ========== */
.quote-box {
  position: relative;
  padding: 24px 24px 24px 48px;

  .quote-mark {
    position: absolute;
    top: 16px;
    left: 18px;
    font-size: 48px;
    color: #E8E8E8;
    font-family: serif;
    font-weight: 900;
    line-height: 1;
  }

  .quote-text {
    font-size: 13px;
    color: #999;
    line-height: 1.7;
    letter-spacing: 0.3px;
  }
}

/* ========== Bottom Button ========== */
.bottom-btn-wrapper {
  padding: 8px 0 0;
}

.bottom-btn {
  width: 100%;
  height: 52px;
  line-height: 52px;
  background: linear-gradient(135deg, #FF8A65, #F25C5C);
  color: #fff;
  text-align: center;
  border-radius: 26px;
  font-size: 18px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 6px 20px rgba(255, 112, 67, 0.35);

  &:active {
    opacity: 0.9;
    transform: scale(0.98);
  }
}

// Loading State
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #f0f0f0;
  border-top-color: #f15b5b;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.loading-text {
  margin-top: 16px;
  font-size: 14px;
  color: #999;
}

// Error State
.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
}

.error-text {
  font-size: 14px;
  color: #f15b5b;
  margin-bottom: 16px;
}

.btn-retry {
  background: #f15b5b;
  color: #fff;
  border: none;
  border-radius: 20px;
  padding: 10px 24px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  &:active {
    opacity: 0.8;
  }
}

.simple-diagnosis {
    font-size: 14px;
    color: #666;
    line-height: 1.6;
    padding: 10px 0;
}

/* ========== Tab Switch ========== */
.tab-switch {
  display: flex;
  background: #fff;
  border-radius: 16px;
  padding: 4px;
  margin-bottom: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);

  .tab-item {
    flex: 1;
    text-align: center;
    padding: 10px 0;
    font-size: 14px;
    font-weight: 600;
    color: #999;
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.2s ease;

    &.active {
      background: #FF7043;
      color: #fff;
      box-shadow: 0 2px 8px rgba(255, 112, 67, 0.3);
    }
  }
}
</style>
